import { InputContainer } from '../InputContainer';
import { InputPropsBase, SelectPropsBase } from '../interfaces/Inputs';
import React, { useRef, useMemo } from 'react';
import { Item } from '../interfaces/Item';
import _uniqueId from 'lodash/uniqueId';
import './styles.scss';
import { Label } from '../Label';
import { CheckboxList } from '../CheckboxList';
import { useRoveFocus } from '../../hooks';
import { DEFAULT_FOCUS_VALUE } from '../../hooks/useRoveFocus';
import { InputType, KEY_ARROW_DOWN, KEY_TAB } from '../models';
import { Alignment } from '../../layout';

interface MultiSelectProps extends InputPropsBase<Array<Item>, Array<Item>>, SelectPropsBase {
    additionalInput?: InputType;
    onAdditionalInputChange?: (newValue: Array<Item>) => void;
    applyAllValue?: number | string | boolean;
    applyAllValueChange?: (value: number | string | boolean) => void;
}
export const defaultRowsToShow = 6;
export const rowHeight = 26;
export const searchBarHeightOffset = 56;
export const spacing = 4;
export const MultiSelectList = (props: MultiSelectProps) => {
    const id = _uniqueId('idMultiSelectList-');

    const defaultHeight = props.showSearchBar
        ? defaultRowsToShow * rowHeight + searchBarHeightOffset
        : defaultRowsToShow * rowHeight + spacing;
    const calculateHeight = props.rowsToShow
        ? props.showSearchBar
            ? (props.rowsToShow as number) * rowHeight + searchBarHeightOffset
            : (props.rowsToShow as number) * rowHeight + spacing
        : defaultHeight;
    const forwardRef = useRef<HTMLUListElement>(null);
    const innerRef = useRef<HTMLDivElement>(null);

    const checkedItems = useMemo(() => {
        return props.value.filter(item => item.checked);
    }, [props.value]);

    const listLength = props.hideSelectAll ? props.value.length : props.value.length + 1;
    const [focus, setFocus] = useRoveFocus(listLength, false, innerRef);

    const labelContent = () => {
        if (props.label) {
            if (!props.showSelectedNumbersInSearchBar) {
                return `${props.label} (${checkedItems.length}/${props.value?.length})`;
            } else {
                return props.label;
            }
        }
    };
    return (
        <InputContainer
            gap={props.gap || 5}
            justify={props.justify}
            align={!props.showSelectedNumbersInSearchBar || props.label ? Alignment.flexStart : props.align}
            direction={props.direction}
            className={props.label ? 'flex-column multiselect-wrapper' : 'flex-column multiselect-wrapper-full'}
            overflow={props.overflow}
        >
            <Label
                content={labelContent()}
                disabled={props.disabled === true}
                labelPosition={props.labelPosition}
                labelLength={props.labelLength || 1}
                errorMessage={props.errorMessage}
                className={props.labelClassName}
            >
                <div
                    id={props.id || id}
                    className={
                        (props.disabled ? 'multiselect-container multiselect-container-disabled' : 'multiselect-container') +
                        (props.className ? ' ' + props.className : '')
                    }
                    style={{ flex: props.inputLength || 1, height: calculateHeight || undefined }}
                    ref={innerRef}
                    onKeyDown={e => {
                        if (e.key === KEY_ARROW_DOWN) {
                            focus === DEFAULT_FOCUS_VALUE && setFocus(0);
                        }
                        if (e.key === KEY_TAB) {
                            setFocus(DEFAULT_FOCUS_VALUE);
                        }
                    }}
                >
                    {props.showSelectedLabels && (
                        <span className="checked-list-checked">
                            {checkedItems.length <= 1
                                ? checkedItems.map(item => item.label).join(', ')
                                : props.selectedLabelsText + ' (' + checkedItems.length + ')'}
                        </span>
                    )}
                    <CheckboxList
                        uncontrolled={props.uncontrolled}
                        disabled={props.disabled}
                        onChange={props.onChange}
                        showSearchBar={props.showSearchBar}
                        rowsToShow={calculateHeight}
                        className={props.className}
                        ref={forwardRef}
                        value={props.value}
                        selectAllText={props.selectAllText}
                        hideSelectAll={props.hideSelectAll}
                        placeholder={props.placeholder}
                        focusItem={focus}
                        setFocusItem={setFocus}
                        showSelectedNumbersInSearchBar={props.showSelectedNumbersInSearchBar}
                        checkedItems={checkedItems.length}
                        label={props.label}
                        additionalInput={props.additionalInput}
                        onAdditionalInputChange={props.onAdditionalInputChange}
                        applyAllValue={props.applyAllValue}
                        applyAllValueChange={props.applyAllValueChange}
                        useTippySingleton
                    />
                </div>
            </Label>
        </InputContainer>
    );
};
